import AppLoader from 'Components/AppLoader';
import { memo } from 'preact/compat';
import { useEffect, useMemo } from 'preact/hooks';
import { useDispatch } from 'react-redux';
import { Provider as UseFetchProvider } from 'use-http';

import { getDeviceType } from '@/helpers';
import { useSelector } from '@/redux/helper';
import { unsetUserData } from '@/redux/slices/user/actions';
import { authRedirect } from '@/shared/fetch';

import ErrorPage from '../ErrorPage';
import { hasPermission } from '.';

const AuthGuard = ({
  permission,
  adminOnly,
  phoneOnly,
  // display fallback component if user is not authorized
  fallback,
  children,
  redirectTo,
  publicPath,
  // we need to pass this to children
  // because we'll have props added by preact router or some other parent
  ...props
}) => {
  const dispatch = useDispatch();
  const role = useSelector((state) => state.user.role);
  const sessionChecked = useSelector((state) => state.user.sessionChecked);
  const activeProductNames = useSelector((state) => state.user.subscription?.activeProductNames);
  const logged = useSelector((state) => state.user.logged);
  const error = useSelector((state) => state.user.sessionError);

  const authorized = useMemo(
    () => hasPermission(permission),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permission, sessionChecked, activeProductNames],
  );
  const useFetchOptions = useMemo(
    () => ({
      interceptors: {
        response: async ({ response }) => {
          if (response.status === 401) {
            dispatch(unsetUserData());
            if (publicPath) {
              authRedirect(publicPath);
            } else if (publicPath !== false) {
              authRedirect();
            }
          }
          return response;
        },
      },
    }),
    [dispatch, publicPath],
  );

  /**
   * Send a visitor with no session somewhere they can be, rather than showing them a 403.
   *
   * Both facts now come from redux, written by `UserSessionSync` — "the session resolved and there
   * is nobody" is `sessionChecked && !logged`, which is what the old `response === null` meant.
   * Nothing here fetches: the session is resolved once, at the app root.
   */
  useEffect(() => {
    if (sessionChecked && !error && !logged && publicPath !== false) {
      authRedirect(publicPath);
    }
  }, [sessionChecked, error, logged, publicPath]);

  if ((!adminOnly && !permission && !phoneOnly) || (adminOnly && permission && !phoneOnly)) {
    throw new Error('AuthGuard expects either "adminOnly", "permission", "phoneOnly", "guestOnly" prop.');
  }

  if (error) {
    return <ErrorPage error="500" message="Oops! Something went wrong." url={props.path} />;
  }

  if (
    (sessionChecked && role === 'admin' && adminOnly) ||
    (sessionChecked && !adminOnly && authorized) ||
    (sessionChecked && phoneOnly && getDeviceType() === 'phone' && authorized)
  ) {
    return <UseFetchProvider options={useFetchOptions}>{children}</UseFetchProvider>;
  }

  if (sessionChecked && !authorized) {
    if (redirectTo) {
      return authRedirect(redirectTo);
    }

    return fallback || <ErrorPage error="403" message="Sorry, you don't have access to this page." url={props.path} />;
  }

  return <AppLoader />;
};

export default memo(AuthGuard);
